import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, Dialog } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import {
  useCommanderPayant,
  useDevisAddon,
  type AddonCatalogue,
  type NaturePayante,
} from '../../api/addons.js';
import { ApiError } from '../../api/errors.js';

export function CommandeAddonDialog({
  galerie,
  addon,
  nature,
  cle,
  fermer,
}: {
  galerie: string;
  addon: AddonCatalogue;
  nature: NaturePayante;
  cle: string;
  fermer: () => void;
}) {
  const { t, i18n } = useTranslation('regie');
  const avecDependances = addon.boiteReception === false;
  const devis = useDevisAddon(galerie, addon.addon, nature, avecDependances);
  const commande = useCommanderPayant(galerie);
  const [accepte, setAccepte] = useState(false);
  const [devisModifie, setDevisModifie] = useState(false);
  const montant = (centimes: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(
      centimes / 100,
    );
  const date = (valeur: string) =>
    formatDate(new Date(valeur), i18n.language === 'en' ? 'en' : 'fr', 'court');
  const raison = (erreur: unknown) =>
    erreur instanceof ApiError ? (erreur.details as { raison?: string } | null)?.raison : undefined;
  const ficheRequise =
    raison(devis.error) === 'fiche_facturation_requise' ||
    raison(devis.error) === 'tva_intracom_requise';
  return (
    <Dialog
      open
      onOpenChange={(ouvert) => {
        if (!ouvert && !commande.isPending) fermer();
      }}
      title={t(`commun:modules.${addon.addon}.libelle`)}
      closeLabel={t('reglages.marketplace.annuler')}
    >
      {devis.isPending ? <p role="status">{t('reglages.marketplace.chargement')}</p> : null}
      {devis.isError ? <p role="alert">{t('reglages.marketplace.devisIndisponible')}</p> : null}
      {ficheRequise ? (
        <Link to={`/${galerie}/reglages/facturation-artosera`}>
          {t('reglages.facturationArtosera.titre')}
        </Link>
      ) : null}
      {devisModifie ? <p role="alert">{t('reglages.marketplace.devisModifie')}</p> : null}
      {devis.data && !devis.isError && !devis.isFetching ? (
        <>
          <ul>
            {devis.data.lignes.map((ligne) => (
              <li key={ligne.addon}>
                <strong>{t(`commun:modules.${ligne.addon}.libelle`)}</strong> ·{' '}
                {t(`reglages.marketplace.formules.${ligne.formule}`)}
                <p>
                  {t('reglages.marketplace.brut')} : {montant(ligne.htBrutCentimes)} ·{' '}
                  {t('reglages.marketplace.remise')} : {montant(ligne.remiseCentimes)} ·{' '}
                  {t('reglages.marketplace.ht')} : {montant(ligne.htNetCentimes)}
                </p>
                {ligne.jusquAu ? (
                  <p>
                    {date(ligne.depuisLe)} – {date(ligne.jusquAu)}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
          <p>
            {t('reglages.marketplace.ht')} : {montant(devis.data.totaux.totalLignesCentimes)}
          </p>
          <p>
            {t('reglages.marketplace.tva')} ({devis.data.tva.categorie}
            {devis.data.tva.categorie !== 'O' ? ` · ${devis.data.tva.taux} %` : ''}) :{' '}
            {montant(devis.data.totaux.totalTvaCentimes)}
          </p>
          {devis.data.tva.mention ? <p>{devis.data.tva.mention}</p> : null}
          <p>
            <strong>
              {t('reglages.marketplace.ttc')} : {montant(devis.data.totaux.totalTtcCentimes)}
            </strong>
          </p>
          <p>
            {t(
              nature === 'achat'
                ? 'reglages.marketplace.acquisition'
                : 'reglages.marketplace.renouvellement',
            )}
          </p>
          <label>
            <input
              type="checkbox"
              checked={accepte}
              onChange={(e) => setAccepte(e.target.checked)}
              disabled={commande.isPending}
            />
            {t('reglages.marketplace.conditions', { version: devis.data.conditionsVersion })}
          </label>
        </>
      ) : null}
      {commande.isError ? <p role="alert">{t('reglages.marketplace.echecCommande')}</p> : null}
      <Button
        disabled={
          !accepte || !devis.data || devis.isError || devis.isFetching || commande.isPending
        }
        onClick={() => {
          if (!devis.data || !accepte) return;
          commande.mutate(
            {
              addon: addon.addon,
              nature,
              cle,
              avecDependances,
              versionsPrix: devis.data.versionPrix,
              conditionsVersion: devis.data.conditionsVersion,
              empreinteDevis: devis.data.empreinteDevis,
            },
            {
              onSuccess: fermer,
              onError: async (erreur) => {
                if (
                  raison(erreur) === 'prix_modifie' ||
                  raison(erreur) === 'devis_modifie' ||
                  raison(erreur) === 'commande_a_reessayer' ||
                  raison(erreur) === 'conditions_modifiees'
                ) {
                  setAccepte(false);
                  setDevisModifie(true);
                  await devis.refetch();
                }
              },
            },
          );
        }}
      >
        {t('reglages.marketplace.confirmerCommande')}
      </Button>
      <Button variant="secondary" disabled={commande.isPending} onClick={fermer}>
        {t('reglages.marketplace.annuler')}
      </Button>
    </Dialog>
  );
}
